import React, { useState, useEffect, useRef } from 'react';
import { X, Search, Download, Upload, ChevronLeft, ChevronRight } from 'lucide-react';
import * as XLSX from 'xlsx';
import api from '../lib/api';
import ClientProfileModal from '../components/ClientProfileModal';

const getToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length === 3) {
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  }
  return dateStr;
};

const BonosPage = () => {
  const [vouchers, setVouchers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [profileClient, setProfileClient] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [clientSearchForm, setClientSearchForm] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [newVoucher, setNewVoucher] = useState({ client_id: '', minutes: 80, purchase_date: getToday() });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 50;
  const fileInputRef = useRef(null);

  const fetchVouchersAndClients = async () => {
    try {
      const [vouchersResponse, clientsResponse] = await Promise.all([
        api.get('/vouchers'),
        api.get('/clients')
      ]);
      const sortedVouchers = [...(vouchersResponse.data || [])].sort((a,b) => {
        const dateA = a.purchase_date || "";
        const dateB = b.purchase_date || "";
        if (dateB === dateA) {
            return (b.id || '').localeCompare(a.id || '');
        }
        return dateB.localeCompare(dateA);
      });
      setVouchers(sortedVouchers);
      setClients(clientsResponse.data || []);
      
      if (profileClient) {
        const updated = clientsResponse.data.find(c => c.id === profileClient.id);
        if (updated) setProfileClient(updated);
      }
    } catch (error) {
      console.error("Error fetching data", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVouchersAndClients();
  }, []);

  const exportToExcelVouchers = () => {
    const dataObj = vouchers.map(v => {
      const clientObj = clients.find(c => c.id === v.client_id);
      return {
        'Cliente ID': v.client_id || '',
        'Nombre Cliente': clientObj ? clientObj.name : 'Desconocido',
        'Fecha Compra': formatDate(v.purchase_date) || '',
        'Minutos': v.minutes || 0
      };
    });
    
    const worksheet = XLSX.utils.json_to_sheet(dataObj);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bonos");
    XLSX.writeFile(workbook, "Bonos_SolPlanet.xlsx");
  };

  const handleImportExcelVouchers = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!window.confirm("⚠️ ALERTA ⚠️: ¿Deseas BORRAR TODO el historial de bonos actual e importarlo de nuevo desde el Excel?")) {
        e.target.value = null;
        return;
    }
    
    setLoading(true);
    try {
        const normalizeText = (text) => {
            if (!text) return "";
            return String(text)
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .toLowerCase()
                .trim();
        };

        const reader = new FileReader();
        reader.onload = async (evt) => {
            try {
                const bstr = evt.target.result;
                const wb = XLSX.read(bstr, { type: 'array' });
                const wsname = wb.SheetNames[0];
                const ws = wb.Sheets[wsname];
                const data = XLSX.utils.sheet_to_json(ws);
                
                if (data.length === 0) {
                    alert("El archivo parece vacío.");
                    setLoading(false);
                    return;
                }

                await api.delete('/vouchers/action/wipe_all');
                let success = 0;
                let failedMatch = 0;
                let detectedKeys = data.length > 0 ? Object.keys(data[0]) : [];
                
                for (let rawRow of data) {
                    const row = {};
                    for (let k in rawRow) row[k.trim().toLowerCase()] = rawRow[k];

                    const clientNameOrId = row["id_cliente"] || row["id"] || row["client_id"] || row["cliente id"] || row["usuario id"] || row["cliente"] || row["nombre"] || row["nombre cliente"] || row["usuario"] || row["socio"] || row["descripcion"] || row["description"] || row["email"];
                    const normalizedTarget = normalizeText(clientNameOrId);
                    
                    if (!normalizedTarget) {
                        console.warn("Fila de bono sin identificador de cliente:", rawRow);
                        failedMatch++;
                        continue;
                    }

                    // 1. Try direct ID match (the 22-char Glide ID)
                    let client = clients.find(c => c.id === String(clientNameOrId).trim());

                    // 2. Fallback to fuzzy matching if no direct ID match
                    if (!client) {
                        client = clients.find(c => {
                            const nC = normalizeText(c.name);
                            const nE = normalizeText(c.email);
                            return nC === normalizedTarget || 
                                   (nE && nE === normalizedTarget) ||
                                   nC.includes(normalizedTarget) || 
                                   normalizedTarget.includes(nC);
                        });
                    }
                    
                    if (!client) {
                        console.warn("No se encontró cliente para el bono:", rawRow);
                        failedMatch++;
                        continue;
                    }

                    let rawDate = row["fecha compra"] || row["fecha_compra"] || row["fecha de compra"] || row["fecha"] || row["purchase_date"] || row["createddate"];
                    if (typeof rawDate === "number") {
                        let d = new Date(Math.round((rawDate - 25569)*86400*1000));
                        rawDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    } else if (typeof rawDate === "string") {
                        const parts = rawDate.split(/[/.-]/);
                        if (parts.length === 3) {
                            if (parts[0].length === 4) rawDate = `${parts[0]}-${parts[1]}-${parts[2]}`;
                            else rawDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
                        }
                    }

                    const voucherData = {
                        client_id: client.id,
                        minutes: parseInt(String(row["tiempo"] || row["minutos"] || row["minutes"] || 50).replace(/[^\d]/g, '')),
                        purchase_date: rawDate
                    };

                    try {
                        await api.post('/vouchers?silent=true', voucherData);
                        success++;
                    } catch(err) { console.error("Row import error", err); }
                }
                
                fetchVouchersAndClients();
                if (success === 0 && data.length > 0) {
                    alert(`IMPORTACIÓN FALLIDA: 0 bonos cargados.\n\nNo he encontrado ningún cliente que coincida. Asegúrate de que la columna con los nombres se llame "Cliente" o "Nombre".\n\nColumnas detectadas en tu Excel: ${detectedKeys.join(", ")}`);
                } else {
                    alert(`Importación finalizada.\n- Éxito: ${success} bonos.\n- Error: ${failedMatch} filas (cliente no encontrado).`);
                }
            } catch (err) {
                console.error(err);
                alert("Error técnico al procesar el Excel.");
                setLoading(false);
            }
        };
        reader.readAsArrayBuffer(file);
    } catch(err) {
        console.error(err);
        setLoading(false);
    }
    e.target.value = null;
  };

  const handleCreateVoucher = async (e) => {
    e.preventDefault();
    if (!newVoucher.client_id || !newVoucher.purchase_date) return;
    try {
      const response = await api.post('/vouchers', newVoucher);
      setVouchers([response.data, ...vouchers].sort((a,b) => b.purchase_date.localeCompare(a.purchase_date)));
      setShowCreateModal(false);
      setNewVoucher({ client_id: '', minutes: 80, purchase_date: getToday() });
      setClientSearchForm('');
    } catch (error) {
      alert("Error al vender el bono.");
      console.error("Error creating voucher", error);
    }
  };

  const filteredVouchers = vouchers.filter(voucher => {
    if (!searchQuery) return true;
    const client = clients.find(c => c.id === voucher.client_id);
    if (!client) return false;
    const searchLower = searchQuery.toLowerCase();
    return (
      (client.name && client.name.toLowerCase().includes(searchLower)) ||
      (client.dni && client.dni.toLowerCase().includes(searchLower)) ||
      (client.email && client.email.toLowerCase().includes(searchLower))
    );
  });

  const totalPages = Math.ceil(filteredVouchers.length / itemsPerPage);
  const paginatedVouchers = filteredVouchers.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-stone-800 tracking-tight">Bonos</h1>
          <p className="text-stone-500">Historial de venta de bonos de minutos</p>
        </div>
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input 
              type="text" 
              placeholder="Buscar cliente..." 
              value={searchQuery}
              onChange={handleSearchChange}
              className="w-full sm:w-64 pl-10 pr-4 py-2 bg-white border border-stone-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            />
            <Search className="absolute left-3 top-2.5 text-stone-400" size={18} />
          </div>
          <input 
            type="file" 
            accept=".xlsx" 
            ref={fileInputRef} 
            onChange={handleImportExcelVouchers} 
            className="hidden" 
          />
          <button 
            onClick={() => fileInputRef.current && fileInputRef.current.click()}
            className="flex items-center gap-2 bg-rose-50 hover:bg-rose-100 text-rose-700 border border-rose-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Sustituir historial de bonos"
          >
            <Upload size={18} />
            Importar
          </button>
          <button 
            onClick={exportToExcelVouchers}
            className="flex items-center gap-2 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 border border-emerald-200 px-4 py-2 rounded-xl font-medium transition-colors whitespace-nowrap"
            title="Descargar listado en Excel (XLSX)"
          >
            <Download size={18} />
            Excel
          </button>
          <button 
            onClick={() => setShowCreateModal(true)}
            className="bg-amber-500 hover:bg-amber-600 text-stone-900 px-4 py-2 rounded-xl font-medium shadow-sm shadow-amber-500/20 transition-colors"
          >
            Vender Bono
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-sm border border-stone-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-stone-500">Cargando bonos...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-stone-50 text-stone-500 border-b border-stone-100">
                  <th className="px-6 py-4 font-medium">Cliente ID</th>
                  <th className="px-6 py-4 font-medium">Fecha de Compra</th>
                  <th className="px-6 py-4 font-medium">Minutos</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVouchers.map((voucher) => {
                  const clientObj = clients.find(c => c.id === voucher.client_id);
                  return (
                    <tr 
                      key={voucher.id} 
                      onClick={() => { if(clientObj) setProfileClient(clientObj); }}
                      className={`border-b border-stone-50 transition-colors ${clientObj ? 'hover:bg-stone-50/50 cursor-pointer' : ''}`}
                    >
                      <td className="px-6 py-4">
                      <div className="font-medium text-stone-800">
                        {clients.find(c => c.id === voucher.client_id)?.name || 'Cliente Eliminado'}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-stone-800">{formatDate(voucher.purchase_date)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full text-sm font-medium bg-emerald-100 text-emerald-800">
                        {voucher.minutes} min
                      </span>
                    </td>
                  </tr>
                  );
                })}
                {filteredVouchers.length === 0 && (
                  <tr>
                    <td colSpan="4" className="px-6 py-8 text-center text-stone-500">
                      No hay bonos registrados.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between px-6 py-4 border-t border-stone-100 bg-stone-50/50 gap-4">
                <div className="text-sm text-stone-500">
                  Mostrando <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> a <span className="font-medium">{Math.min(currentPage * itemsPerPage, filteredVouchers.length)}</span> de <span className="font-medium">{filteredVouchers.length}</span> resultados
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="text-sm text-stone-600 font-medium px-2">
                    Página {currentPage} de {totalPages}
                  </span>
                  <button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="p-1.5 rounded-lg border border-stone-200 text-stone-500 hover:bg-stone-100 disabled:opacity-50 disabled:cursor-not-allowed transition bg-white shadow-sm"
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-xl">
            <div className="p-4 border-b border-stone-100 flex items-center justify-between">
              <h3 className="font-semibold text-stone-800">Vender Bono</h3>
              <button onClick={() => setShowCreateModal(false)} className="text-stone-400 hover:text-stone-600">
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleCreateVoucher} className="p-6 space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-stone-700 mb-1">Cliente *</label>
                <input 
                  type="text"
                  placeholder="Buscar por nombre..."
                  value={clientSearchForm}
                  onChange={(e) => {
                    setClientSearchForm(e.target.value);
                    setShowClientDropdown(true);
                    setNewVoucher({...newVoucher, client_id: ''});
                  }}
                  onFocus={() => setShowClientDropdown(true)}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-emerald-500 bg-white"
                  required={!newVoucher.client_id}
                />
                
                {showClientDropdown && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowClientDropdown(false)}></div>
                    <div className="absolute z-20 w-full mt-1 bg-white border border-stone-200 rounded-xl shadow-lg max-h-60 overflow-y-auto custom-scrollbar">
                      {clients.filter(c => c.name.toLowerCase().includes(clientSearchForm.toLowerCase())).map(c => (
                        <div 
                          key={c.id}
                          className="px-4 py-2 hover:bg-stone-50 cursor-pointer border-b border-stone-50 last:border-0"
                          onClick={() => {
                            setNewVoucher({...newVoucher, client_id: c.id});
                            setClientSearchForm(`${c.name}`);
                            setShowClientDropdown(false);
                          }}
                        >
                          <div className="font-medium text-stone-800">{c.name}</div>
                          <div className="text-xs text-stone-500">{c.remaining_minutes} min disponibles</div>
                        </div>
                      ))}
                      {clients.filter(c => c.name.toLowerCase().includes(clientSearchForm.toLowerCase())).length === 0 && (
                        <div className="px-4 py-3 text-sm text-stone-500 text-center">No se encontraron clientes</div>
                      )}
                    </div>
                  </>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Fecha de Compra *</label>
                <input 
                  type="date" 
                  value={newVoucher.purchase_date} 
                  onChange={(e) => setNewVoucher({...newVoucher, purchase_date: e.target.value})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Tipo de Bono *</label>
                <select 
                  value={newVoucher.minutes} 
                  onChange={(e) => setNewVoucher({...newVoucher, minutes: parseInt(e.target.value)})}
                  className="w-full border border-stone-200 rounded-xl px-4 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500 bg-white"
                  required
                >
                  <option value={80}>Bono 80 min</option>
                  <option value={176}>Bono 176 min</option>
                  <option value={272}>Bono 272 min</option>
                </select>
              </div>
              <div className="pt-4 flex justify-end gap-3">
                <button 
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 text-stone-500 hover:text-stone-700 font-medium"
                >
                  Cancelar
                </button>
                <button 
                  type="submit"
                  className="bg-amber-500 text-stone-900 px-4 py-2 rounded-xl font-medium hover:bg-amber-600 transition"
                >
                  Confirmar Venta
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {profileClient && (
        <ClientProfileModal 
          client={profileClient} 
          onClose={() => setProfileClient(null)} 
          onUpdate={fetchVouchersAndClients} 
        />
      )}
    </div>
  );
};

export default BonosPage;
